import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { api } from '../api/client';
import { EMPTY, hydrateState, payloadOf, publicUrlOf, withSchedule, type EditorState } from './articleEditor.helpers';
import { useAutosave, useLeaveGuard } from './useAutosave';
import { formatTaipeiMinute, normalizeScheduleTime, validateScheduleTime } from '../lib/schedule';

export type { EditorState } from './articleEditor.helpers';

/**
 * 明確的儲存動作（狀態只會經由這些按鈕改變，自動儲存永不翻動狀態）：
 * - content：只存內容，不改狀態（「儲存草稿」／「儲存變更」）
 * - publish：先存內容再立即發佈（POST /publish：設 published_at、清前台快取）
 * - schedule：存內容並設為排程刊登（scheduled_at 秒數歸零）
 */
export type SaveIntent = { kind: 'content' } | { kind: 'publish' } | { kind: 'schedule'; at: number };

/** 載入文章詳情、保存編輯狀態、手動＋自動儲存 mutation。 */
export function useArticleEditor(id: string) {
  const qc = useQueryClient();
  const [state, setState] = useState<EditorState>(EMPTY);
  const stateRef = useRef(state);
  stateRef.current = state;
  // 已把哪一篇的資料灌進 state。Tiptap 只在初始化讀一次 content，
  // 若在 detail 載完、但 state 尚未灌入的那一個 render 就掛載，會永遠空白。
  // 因此 loading 必須撐到「當前 id 的資料真的進 state」為止（見 ArticleEditPage 的 Spin 守門）。
  const [hydratedId, setHydratedId] = useState<string | null>(null);
  const hydratedRef = useRef<string | null>(null);

  const detail = useQuery({ queryKey: ['article', id], queryFn: () => api.getArticle(id) });

  useEffect(() => {
    if (!detail.data) return;
    const next = hydrateState(detail.data);
    if (hydratedRef.current === id) {
      // 之後的 refetch（儲存後 / 視窗聚焦）只同步伺服器擁有的狀態欄位，不覆蓋編輯中的內容。
      setState((s) => ({ ...s, status: next.status, scheduledAt: next.scheduledAt }));
      return;
    }
    hydratedRef.current = id;
    setState(next);
    setHydratedId(id);
  }, [detail.data, id]);

  const patch = (p: Partial<EditorState>) => setState((s) => ({ ...s, ...p }));
  const hydrated = hydratedId === id;

  // 明確儲存成功後，把自動儲存的基準對齊「送出當下」的內容（避免緊接著又觸發一次自動儲存）。
  const markSavedRef = useRef<(snapshot?: string) => void>(() => {});

  const save = useMutation({
    mutationFn: async (intent: SaveIntent) => {
      const payload = payloadOf(stateRef.current);
      const snapshot = JSON.stringify(payload);
      if (intent.kind !== 'content' && !stateRef.current.title.trim()) {
        throw new Error('請先填寫文章標題再發佈');
      }
      if (intent.kind === 'schedule') {
        const err = validateScheduleTime(intent.at);
        if (err) throw new Error(err);
        await api.saveArticle(id, withSchedule(payload, normalizeScheduleTime(intent.at)));
      } else {
        await api.saveArticle(id, payload);
        if (intent.kind === 'publish') await api.publishArticle(id);
      }
      return snapshot;
    },
    onSuccess: (snapshot, intent) => {
      markSavedRef.current(snapshot);
      if (intent.kind === 'publish') {
        patch({ status: 'published' });
        message.success('已發佈，前台已上線');
      } else if (intent.kind === 'schedule') {
        const at = normalizeScheduleTime(intent.at);
        patch({ status: 'scheduled', scheduledAt: at });
        message.success(`已排程，將於 ${formatTaipeiMinute(at)}（台北時間）自動發佈`);
      } else {
        message.success(stateRef.current.status === 'draft' ? '已儲存草稿（尚未發佈）' : '已儲存變更');
      }
      qc.invalidateQueries({ queryKey: ['article', id] });
      qc.invalidateQueries({ queryKey: ['articles'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const serialized = useMemo(() => JSON.stringify(payloadOf(state)), [state]);
  const autosave = useAutosave({
    enabled: hydrated,
    resetKey: id,
    serialized,
    guardOk: true,
    // 自動儲存靜默送出最新內容（不含狀態）；不噴成功 toast、不 invalidate（避免打斷輸入中的內容）。
    save: () => api.saveArticle(id, payloadOf(stateRef.current)),
  });
  markSavedRef.current = autosave.markSaved;

  // 有未儲存變更 / 儲存進行中 → 關閉分頁跳原生確認；站內導航離開時 flush 一次。
  useLeaveGuard(autosave.isDirty || autosave.saving || save.isPending, autosave.flush);

  return {
    state,
    patch,
    save,
    autosave,
    // 撐住 Spin 直到當前 id 的資料灌進 state，避免 Tiptap 用空 state 掛載後永遠空白。
    loading: detail.isLoading || !hydrated,
    legacyHtml: detail.data?.article.bodyHtmlLegacy ?? null,
    publicUrl: publicUrlOf(detail.data, state.status),
  };
}
